
import { Component, Vue, Watch } from 'vue-property-decorator';
import InsightPanel from '@/components/insights/InsightPanel.vue';
import { TopicModel, ITopic } from '@/models/topic';
import { EventBus } from '@/main';
import { PinnedQuestionModel } from '@/models/pinned_question';

// No longer needed - measure/dimension codes are embedded in topic data

@Component({
  components: {
    InsightPanel,
  },
})
export default class TopicDetail extends Vue {
  // Route props
  public topicCode: string = '';

  // Component state
  public topicData: ITopic | null = null;
  public isLoading: boolean = false;
  public error: string = '';
  public insightPanelKey: number = 0;

  // InsightPanel props
  public measureCode: string = '';
  public dimensionCode: string = '';
  public analyticsType: string = 'standard';
  public filterColumns: any[] = [];
  public reportParams: any = {};
  public reportColumns: any[] = [];
  public productSegment: any = null;
  public pivotOption: any = null;
  public sortBy: string = '';
  public sortDesc: boolean = false;
  public calculatedMetrics: any[] = [];
  public comparisonDate: string | null = null;
  public filterDimensions: any[] = [];

  // Topic mappings no longer needed - codes are embedded in topic data

  // Computed properties
  public get topicDisplayName(): string {
    return this.topicData?.display_name || this.formatTopicCode(this.topicCode);
  }

  public get breadcrumbItems() {
    return [
      {
        text: 'Insights Hub',
        icon: 'mdi-lightbulb-on-outline',
        disabled: false,
        click: this.navigateToHub,
      },
      {
        text: this.topicDisplayName,
        disabled: true,
        // No click handler for disabled items
      },
    ];
  }

  // Lifecycle hooks
  public async created(): Promise<void> {
    this.topicCode = this.$route.params.topicCode || '';
    await this.initializeTopic();
  }

  @Watch('$route.params.topicCode')
  public async onTopicCodeChanged(newTopicCode: string): Promise<void> {
    this.topicCode = newTopicCode;
    await this.initializeTopic();
  }

  // Methods
  public async initializeTopic(): Promise<void> {
    if (!this.topicCode) {
      this.error = 'No topic code provided';
      return;
    }

    this.isLoading = true;
    this.error = '';

    try {
      // Get topic data using TopicModel
      this.topicData = await TopicModel.find(this.topicCode);
      
      if (this.topicData && this.topicData.measureCode) {
        // Use measure/dimension codes directly from topic data
        this.measureCode = this.topicData.measureCode;
        this.dimensionCode = this.topicData.dimensionCode || '';
        this.analyticsType = 'standard'; // Can be enhanced per topic later
        
        // Force InsightPanel re-render with new data
        this.insightPanelKey = Date.now();
      } else if (this.topicData) {
        this.error = `Topic "${this.topicCode}" is missing required measureCode`;
      } else {
        this.error = `Topic not found: ${this.topicCode}`;
      }

    } catch (error) {
      this.error = error instanceof Error ? error.message : 'An unexpected error occurred';
      this.topicData = null;
    } finally {
      this.isLoading = false;
    }
  }


  // Helper methods for InsightPanel integration
  public getMeasureObject(): any {
    if (!this.topicData) return null;
    return TopicModel.getMeasureObject(this.topicData);
  }

  public getDimensionObject(): any {
    if (!this.topicData) return null;
    return TopicModel.getDimensionObject(this.topicData);
  }

  public getDefaultTimerange(): string[] {
    // Default to last 3 months as requested - using same logic as DateFilter
    const now = new Date();
    const startDate = new Date();
    startDate.setMonth(now.getMonth() - 3);
    
    const endDate = new Date();
    endDate.setDate(now.getDate() - 1); // End date is yesterday
    
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return [
      formatDate(startDate), // Start date (3 months ago)
      formatDate(endDate),   // End date (yesterday)
      '3m'                   // Range type matching DateFilter
    ];
  }

  public getReportParams(): any {
    // Topic-specific report parameters
    return {
      topic_code: this.topicCode,
      analytics_type: this.analyticsType,
    };
  }

  // Formatting helpers
  public formatTopicCode(code: string): string {
    return code.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  public formatCategory(category: string): string {
    return TopicModel.formatCategoryName(category as ITopic['category']);
  }

  public getCategoryColor(category: string): string {
    return TopicModel.getCategoryColor(category as ITopic['category']);
  }

  // Navigation methods
  public navigateToHub(): void {
    this.$router.push({ name: 'insights-hub' });
  }

  // InsightPanel event handlers
  public onInsightsLoaded(data: any): void {
    // Handle successful insights loading
    console.log('Topic insights loaded:', data);
  }

  public onInsightsError(error: any): void {
    // Handle insights loading error
    console.error('Topic insights error:', error);
    this.error = 'Failed to load insights for this topic';
  }

  public onInsightsProgress(progress: any): void {
    // Handle insights loading progress
    console.log('Topic insights progress:', progress);
  }

  public async onQuestionPinned(data: any): Promise<void> {
    // Handle question pinned event
    console.log('Question pinned:', data);
    
    try {
      // Extract question data from the event
      const { questionId, question: questionData } = data;
      
      console.log('Question pinning data received:', { questionId, questionData });
      
      if (!questionData) {
        console.error('No question data provided for pinning');
        EventBus.$emit('show-snackbar', {
          message: 'Failed to pin question: Missing question data',
          color: 'error',
          timeout: 3000
        });
        return;
      }
      
      if (!questionData.question) {
        console.error('Question text is missing from questionData:', questionData);
        EventBus.$emit('show-snackbar', {
          message: 'Failed to pin question: Question text is missing',
          color: 'error',
          timeout: 3000
        });
        return;
      }

      // Generate question hash (simple hash based on question text + instruction)
      const questionContent = questionData.question + (questionData.instruction || '');
      const questionHash = this.generateQuestionHash(questionContent);

      // Prepare parameters object as expected by backend
      const parameters = {
        measures: [this.topicData?.measureCode || this.measureCode],
        dimensions: this.topicData?.dimensionCode || this.dimensionCode ? [this.topicData?.dimensionCode || this.dimensionCode] : []
      };

      // Create a new pinned question model with correct structure
      const pinnedQuestion = new PinnedQuestionModel({
        question_text: questionData.question,
        instruction: questionData.instruction,
        question_hash: questionHash,
        parameters: parameters,
        measure_code: this.topicData?.measureCode || this.measureCode,
        dimension_code: this.topicData?.dimensionCode || this.dimensionCode,
        category: this.mapMeasureToCategory(this.topicData?.measureCode || this.measureCode) || questionData.category?.code || 'general_query',
        priority: questionData.priority || 'medium',
        pin_reason: 'user_preference',
        execution_count: 0,
        last_executed_at: new Date().toISOString(),
        metadata: {
          priority: questionData.priority || 'medium',
          pin_context: `Pinned from topic: ${this.topicCode} (Original ID: ${questionId})`
        } as any
      });

      // Save to backend
      const success = await pinnedQuestion.save();

      if (success) {
        EventBus.$emit('show-snackbar', {
          message: 'Question pinned successfully',
          color: 'success',
          timeout: 3000
        });
      } else {
        EventBus.$emit('show-snackbar', {
          message: 'Failed to pin question',
          color: 'error',
          timeout: 3000
        });
      }
    } catch (error) {
      console.error('Error pinning question:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Error occurred while pinning question',
        color: 'error',
        timeout: 3000
      });
    }
  }

  // Helper methods
  private mapMeasureToCategory(measureCode: string): string {
    const measureToCategoryMap: { [key: string]: string } = {
      'sales_overview': 'sales_overview',
      'inventory_forecasting': 'inventory_forecasting', 
      'customer_insights': 'customer_insights',
      'product_analytics': 'product_analytics',
      'order_analytics': 'order_analytics',
      'financial_reporting': 'financial_reporting',
      'marketing_analytics': 'marketing_analytics',
      'operations_monitoring': 'operations_monitoring',
      'supplier_analytics': 'supplier_analytics'
    };
    
    return measureToCategoryMap[measureCode] || 'general_query';
  }

  private generateQuestionHash(content: string): string {
    // Generate a 64-character SHA-256-like hash
    // Simple implementation that produces a consistent 64-char hash
    let hash = '';
    
    // Use multiple passes to create a longer hash
    for (let i = 0; i < 4; i++) {
      let h = 0;
      const input = content + i.toString();
      
      for (let j = 0; j < input.length; j++) {
        const char = input.charCodeAt(j);
        h = ((h << 5) - h) + char;
        h = h & h; // Convert to 32-bit integer
      }
      
      // Convert to hex and pad to 16 characters
      hash += Math.abs(h).toString(16).padStart(16, '0');
    }
    
    // Ensure exactly 64 characters
    return hash.substring(0, 64);
  }

  // Component cleanup
  public beforeDestroy(): void {
    // Any cleanup needed
  }
}
